<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="max-w-7xl mx-auto">
      <!-- 頁面標題 -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          <a-icon type="bar-chart" class="mr-2" />
          廣告數據分析
        </h1>
        <p class="text-gray-600">查詢各機台的廣告曝光與互動數據</p>
      </div>

      <!-- 篩選條件區域 - 橫向佈局 -->
      <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4"
        >
          <!-- 1. 選擇機台群組 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >機台群組</label
            >
            <a-select
              v-model="searchForm.group_id"
              placeholder="請選擇群組"
              style="width: 100%"
              @change="handleGroupChange"
              :loading="loadingGroups"
            >
              <a-select-option
                v-for="group in groupOptions"
                :key="group.id"
                :value="group.id"
              >
                <div class="flex justify-between items-center">
                  <span>{{ group.name }}</span>
                  <a-tag color="blue" size="small"
                    >{{
                      group.stores_data ? group.stores_data.length : 0
                    }}
                    家</a-tag
                  >
                </div>
              </a-select-option>
            </a-select>
          </div>

          <!-- 2. 選擇檔期 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >廣告檔期</label
            >
            <a-select
              v-model="searchForm.campaign_id"
              placeholder="請選擇檔期"
              style="width: 100%"
              @change="handleCampaignChange"
              :loading="loadingCampaigns"
              :disabled="!searchForm.group_id"
            >
              <a-select-option
                v-for="campaign in campaignOptions"
                :key="campaign.campaign_id"
                :value="campaign.campaign_id"
              >
                <div>
                  <div>{{ campaign.campaign_name }}</div>
                  <div class="text-xs text-gray-500">
                    {{
                      formatDateRange(
                        campaign.start_datetime,
                        campaign.end_datetime
                      )
                    }}
                    <a-tag
                      :color="getCampaignStatusColor(campaign)"
                      size="small"
                      class="ml-1"
                    >
                      {{ getCampaignStatusText(campaign) }}
                    </a-tag>
                  </div>
                </div>
              </a-select-option>
            </a-select>
          </div>

          <!-- 3. 選擇版位 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              廣告版位
              <span
                v-if="selectedCampaign && selectedCampaign.slot_id"
                class="text-xs text-blue-500 ml-1"
              >
                (依檔期自動設定)
              </span>
            </label>
            <a-select
              v-model="searchForm.slot_id"
              placeholder="請選擇版位"
              style="width: 100%"
              :disabled="!searchForm.group_id || !!selectedCampaign"
            >
              <a-select-option :value="1"> 版位1 (主廣告區) </a-select-option>
              <a-select-option :value="2"> 版位2 (次廣告區) </a-select-option>
              <a-select-option :value="3"> 版位3 (輔助廣告區) </a-select-option>
            </a-select>
            <div
              v-if="selectedCampaign && selectedCampaign.slot_id"
              class="text-xs text-gray-400 mt-1"
            >
              此檔期綁定版位{{ selectedCampaign.slot_id }}
            </div>
          </div>

          <!-- 4. 選擇店家/設備 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              店家/設備
              <span class="text-gray-400 text-xs ml-1"
                >(已選 {{ selectedDeviceMacs.length }})</span
              >
            </label>
            <a-select
              mode="multiple"
              v-model="selectedDeviceMacs"
              placeholder="請選擇店家"
              style="width: 100%"
              :disabled="!searchForm.group_id"
              :max-tag-count="2"
              :max-tag-placeholder="
                (omittedValues) => `+${omittedValues.length}...`
              "
            >
              <a-select-option
                v-for="store in storeOptions"
                :key="store.device_mac"
                :value="store.device_mac"
              >
                <a-badge
                  :status="store.status === 'active' ? 'success' : 'error'"
                />
                {{ store.store_name }}
              </a-select-option>
            </a-select>
            <div class="mt-1">
              <a-button
                type="link"
                size="small"
                class="p-0"
                @click="selectAllStores"
                :disabled="!searchForm.group_id"
              >
                全選
              </a-button>
              <a-divider type="vertical" />
              <a-button
                type="link"
                size="small"
                class="p-0"
                @click="clearAllStores"
                :disabled="!searchForm.group_id"
              >
                清除
              </a-button>
            </div>
          </div>

          <!-- 5. 選擇日期範圍 -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >日期範圍</label
            >
            <a-range-picker
              v-model="dateRange"
              format="YYYY-MM-DD"
              :placeholder="['開始日期', '結束日期']"
              @change="onDateChange"
              :disabled="!searchForm.group_id"
              style="width: 100%"
            />
          </div>
        </div>

        <!-- 操作按鈕 -->
        <div class="flex justify-end space-x-3 border-t pt-4">
          <a-button @click="resetSearch">
            <a-icon type="undo" />
            重置
          </a-button>
          <a-button
            type="primary"
            @click="handleSearch"
            :loading="loading"
            :disabled="!canSearch"
          >
            <a-icon type="search" />
            查詢數據
          </a-button>

        </div>
      </div>

      <!-- 查詢進度顯示 -->
      <div
        v-if="queryStatus.isQuerying"
        class="bg-white rounded-lg shadow-sm p-6 mb-6"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">
            <a-icon type="loading" spin class="mr-2" />
            正在查詢中...
          </h3>
          <a-tag :color="getQueryStatusColor()">
            {{ queryStatus.respondedDevices }} /
            {{ queryStatus.totalDevices }} 台已回應
          </a-tag>
        </div>
        <a-progress
          :percent="queryProgress"
          :status="queryStatus.status === 'completed' ? 'success' : 'active'"
          :stroke-color="{
            '0%': '#108ee9',
            '100%': '#87d068',
          }"
        />
        <div class="mt-2 text-sm text-gray-500">
          預計最長等待時間：{{ queryStatus.timeoutSeconds || 20 }} 秒
        </div>
      </div>

      <!-- 查詢結果區域 -->
      <div v-if="hasResults" class="space-y-6">
        <!-- 結果標題 + 視圖切換 -->
        <div class="bg-white rounded-lg shadow-sm p-4">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <div class="flex items-center">
              <h3 class="text-lg font-semibold mr-4">
                <a-icon type="bar-chart" class="mr-2 text-blue-500" />
                查詢結果 - {{ getSlotName(searchForm.slot_id) }}
              </h3>
              <a-tag
                :color="queryStatus.status === 'completed' ? 'green' : 'orange'"
              >
                {{
                  queryStatus.status === "completed"
                    ? "✅ 全部完成"
                    : "⏳ 部分完成"
                }}
              </a-tag>
              <a-tag color="blue" class="ml-2">
                {{ reportData.length }} 台設備
              </a-tag>
            </div>

            <div class="flex items-center gap-3">
              <!-- 視圖切換按鈕 -->
              <a-radio-group
                v-model="viewMode"
                button-style="solid"
                size="small"
              >
                <a-radio-button value="summary">
                  <a-icon type="pie-chart" /> 彙總
                </a-radio-button>
                <a-radio-button value="table">
                  <a-icon type="table" /> 表格
                </a-radio-button>
              </a-radio-group>

              <!-- 匯出功能下拉選單 -->
              <a-dropdown :trigger="['click']">
                <a-button :loading="exporting" size="small">
                  <a-icon type="download" />
                  匯出報表
                  <a-icon type="down" />
                </a-button>
                <a-menu slot="overlay" @click="handleExportMenuClick">
                  <a-menu-item key="complete">
                    <a-icon type="file-excel" />
                    匯出完整報表 (含明細)
                  </a-menu-item>
                  <a-menu-item key="summary">
                    <a-icon type="pie-chart" />
                    匯出彙總統計 (僅數據)
                  </a-menu-item>
                </a-menu>
              </a-dropdown>
            </div>
          </div>
        </div>

        <!-- ========== 彙總視圖 ========== -->
        <div v-if="viewMode === 'summary'" class="space-y-6">
          <!-- 數據總覽卡片 -->
          <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div class="bg-white rounded-lg shadow-sm p-6 text-center">
              <div class="text-3xl font-bold text-blue-600">
                {{ formatNumber(summaryTotals.totalAppear) }}
              </div>
              <div class="text-sm text-gray-500 mt-1">總曝光次數</div>
            </div>
            <div class="bg-white rounded-lg shadow-sm p-6 text-center">
              <div class="text-3xl font-bold text-purple-600">
                {{ formatNumber(summaryTotals.totalAnswer) }}
              </div>
              <div class="text-sm text-gray-500 mt-1">總作答/點擊次數</div>
            </div>
            <div class="bg-white rounded-lg shadow-sm p-6 text-center">
              <div class="text-3xl font-bold text-gray-500">
                {{ formatNumber(summaryTotals.totalNoAnswer) }}
              </div>
              <div class="text-sm text-gray-500 mt-1">未作答次數</div>
            </div>
            <div class="bg-white rounded-lg shadow-sm p-6 text-center">
              <div class="text-3xl font-bold text-green-600">
                {{ formatNumber(summaryTotals.totalCorrect) }}
              </div>
              <div class="text-sm text-gray-500 mt-1">答對次數</div>
            </div>
            <div class="bg-white rounded-lg shadow-sm p-6 text-center">
              <div class="text-3xl font-bold text-red-600">
                {{ formatNumber(summaryTotals.totalWrong) }}
              </div>
              <div class="text-sm text-gray-500 mt-1">答錯次數</div>
            </div>
          </div>

          <!-- 設備回應狀態 -->
          <div class="bg-white rounded-lg shadow-sm p-6">
            <h4 class="font-semibold mb-4">設備回應狀態</h4>
            <div class="flex items-center gap-8">
              <div class="flex items-center">
                <a-badge status="success" />
                <span class="ml-2">已回應: {{ respondedDeviceCount }} 台</span>
              </div>
              <div class="flex items-center">
                <a-badge status="error" />
                <span class="ml-2">離線/未回應: {{ unresponsiveDeviceCount }} 台</span>
              </div>
              <div class="flex items-center">
                <a-badge status="default" />
                <span class="ml-2">回應率: {{ responseRate }}%</span>
              </div>
            </div>
            <a-progress
              :percent="parseFloat(responseRate)"
              :stroke-color="{ '0%': '#108ee9', '100%': '#87d068' }"
              class="mt-4"
            />
          </div>

          <!-- 按日期彙總 (如果是問答) -->
          <div
            v-if="searchForm.slot_id === 1 && hasQuizData"
            class="bg-white rounded-lg shadow-sm p-6"
          >
            <h4 class="font-semibold mb-4">按日期彙總</h4>
            <a-table
              :columns="dateSummaryColumns"
              :data-source="dateSummaryData"
              :pagination="false"
              size="middle"
              row-key="date"
            />
          </div>
        </div>

        <!-- 🆕 ========== 檔期圖片展示區 ========== -->
        <div
          v-if="selectedCampaign && campaignImages.length > 0"
          class="bg-white rounded-lg shadow-sm p-4 mb-4"
        >
          <div class="flex items-center justify-between mb-3">
            <h4 class="font-semibold text-gray-700">
              <a-icon type="picture" class="mr-2" />
              檔期廣告素材 ({{ campaignImages.length }} 張)
            </h4>
            <a-tag v-if="selectedCampaign.ad_type === 'quiz'" color="purple"
              >問答廣告</a-tag
            >
            <a-tag v-else color="blue">輪播廣告</a-tag>
          </div>
          <div class="flex flex-wrap gap-3">
            <div
              v-for="(img, index) in campaignImages"
              :key="index"
              class="relative group cursor-pointer"
              @click="showImagePreview(getCampaignImageUrl(img))"
            >
              <img
                :src="getCampaignImageUrl(img)"
                :alt="img"
                class="w-24 h-24 object-cover rounded-lg border border-gray-200 transition-transform group-hover:scale-105 group-hover:shadow-md"
                @error="handleImageError"
              />
              <div
                class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg truncate"
              >
                {{ img }}
              </div>
            </div>
          </div>
          <div v-if="loadingCampaignImages" class="text-center py-4">
            <a-spin tip="載入圖片中..." />
          </div>
        </div>

        <!-- ========== 表格視圖 ========== -->
        <div v-if="viewMode === 'table'" class="bg-white rounded-lg shadow-sm">
          <!-- 問答廣告表格 -->
          <a-table
            v-if="searchForm.slot_id === 1 && hasQuizData"
            :columns="quizTableColumns"
            :data-source="flattenedQuizData"
            :pagination="{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆`,
            }"
            :scroll="{ x: 800 }"
            size="middle"
            row-key="uniqueKey"
          >
            <template slot="storeName" slot-scope="text, record">
              <div>
                <div class="font-medium">{{ record.store_name }}</div>
                <div class="text-xs text-gray-400">{{ record.device_mac }}</div>
              </div>
            </template>
            <template slot="date" slot-scope="text">
              <a-tag color="blue">{{ text }}</a-tag>
            </template>
          </a-table>

          <!-- 圖片廣告表格 -->
          <a-table
            v-else
            :columns="imageTableColumns"
            :data-source="flattenedImageData"
            :pagination="{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆`,
            }"
            :scroll="{ x: 800 }"
            size="middle"
            row-key="uniqueKey"
          >
            <template slot="storeName" slot-scope="text, record">
              <div>
                <div class="font-medium">{{ record.store_name }}</div>
                <div class="text-xs text-gray-400">{{ record.device_mac }}</div>
              </div>
            </template>
            <template slot="date" slot-scope="text">
              <a-tag v-if="text" color="blue">{{ text }}</a-tag>
              <span v-else class="text-gray-400">-</span>
            </template>
          </a-table>
        </div>

        <!-- 離線設備提示 -->
        <div
          v-if="offlineDevices.length > 0"
          class="bg-orange-50 rounded-lg shadow-sm p-6"
        >
          <div class="flex items-center text-orange-700 mb-3">
            <a-icon type="warning" class="text-xl mr-2" />
            <span class="font-semibold"
              >離線/未回應設備 ({{ offlineDevices.length }} 台)</span
            >
          </div>
          <div class="space-y-2">
            <div
              v-for="mac in offlineDevices"
              :key="mac"
              class="flex items-center text-orange-600"
            >
              <a-icon type="disconnect" class="mr-2" />
              <span>{{ getStoreNameByMac(mac) }} ({{ mac }})</span>
              <span class="text-sm ml-2">- 該設備可能離線或網路異常</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 空狀態 -->
      <div
        v-if="!loading && !queryStatus.isQuerying && !hasResults"
        class="bg-white rounded-lg shadow-sm p-12 text-center"
      >
        <a-icon type="file-search" class="text-6xl text-gray-300 mb-4" />
        <p class="text-gray-500 text-lg">請選擇查詢條件後點擊「查詢數據」</p>
      </div>

      <!-- 圖片預覽模態窗 -->
      <a-modal
        :visible="previewVisible"
        :footer="null"
        @cancel="previewVisible = false"
        width="80%"
        centered
      >
        <img
          :src="previewImage"
          style="width: 100%; max-height: 70vh; object-fit: contain"
        />
      </a-modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { EventSourcePolyfill } from 'event-source-polyfill'

export default {
  name: 'AdTrackingReport',
  data () {
    return {
      moment,
      baseURL: this.GLOBAL.baseURL,
      basePort: this.GLOBAL.basePort,

      // 搜尋條件
      searchForm: {
        group_id: undefined,
        campaign_id: undefined,
        slot_id: undefined,
        date_from: undefined,
        date_to: undefined
      },
      dateRange: [moment().subtract(30, 'days'), moment()],

      // 多選設備
      selectedDeviceMacs: [],

      // 選項資料
      groupOptions: [],
      campaignOptions: [],
      storeOptions: [],
      selectedCampaign: null,

      // 載入狀態
      loading: false,
      loadingGroups: false,
      loadingCampaigns: false,
      exporting: false,

      // 查詢狀態 (SSE)
      queryStatus: {
        isQuerying: false,
        requestId: null,
        totalDevices: 0,
        respondedDevices: 0,
        timeoutSeconds: 20,
        status: null // 'pending', 'completed', 'timeout'
      },
      eventSource: null, // SSE EventSource 物件

      // 結果資料
      reportData: [],
      offlineDevices: [],
      summaryStats: null,

      // 🆕 檔期圖片展示
      campaignImages: [], // 當前檔期的圖片列表
      loadingCampaignImages: false,

      // 圖片預覽
      previewVisible: false,
      previewImage: '',

      // 🆕 視圖模式控制
      viewMode: 'summary', // 'summary' | 'table'

      // 🆕 表格欄位定義 - 問答廣告
      quizTableColumns: [
        {
          title: '店家名稱',
          dataIndex: 'store_name',
          key: 'store_name',
          width: 180,
          scopedSlots: { customRender: 'storeName' }
        },
        {
          title: '日期',
          dataIndex: 'date',
          key: 'date',
          width: 120,
          scopedSlots: { customRender: 'date' },
          sorter: (a, b) => a.date.localeCompare(b.date)
        },
        {
          title: '題目',
          dataIndex: 'question_title',
          key: 'question_title',
          ellipsis: true
        },
        {
          title: '出現次數',
          dataIndex: 'question_appear_count',
          key: 'appear',
          width: 100,
          sorter: (a, b) => a.question_appear_count - b.question_appear_count
        },
        {
          title: '作答次數',
          dataIndex: 'question_answered_count',
          key: 'answer',
          width: 100,
          sorter: (a, b) =>
            a.question_answered_count - b.question_answered_count
        },
        {
          title: '未作答',
          dataIndex: 'no_answer_count',
          key: 'no_answer',
          width: 80,
          sorter: (a, b) => a.no_answer_count - b.no_answer_count
        },
        {
          title: '答對',
          dataIndex: 'correct_count',
          key: 'correct',
          width: 80,
          sorter: (a, b) => a.correct_count - b.correct_count
        },
        {
          title: '作答率',
          dataIndex: 'response_rate',
          key: 'response_rate',
          width: 100,
          sorter: (a, b) => parseFloat(a.response_rate) - parseFloat(b.response_rate)
        },
        {
          title: '正確率',
          dataIndex: 'accuracy_rate',
          key: 'accuracy_rate',
          width: 100,
          sorter: (a, b) => parseFloat(a.accuracy_rate) - parseFloat(b.accuracy_rate)
        },
        {
          title: '答錯',
          dataIndex: 'wrong_count',
          key: 'wrong',
          width: 80,
          sorter: (a, b) => a.wrong_count - b.wrong_count
        },
        {
          title: '選項1答數',
          dataIndex: 'option_1_count',
          key: 'option_1',
          width: 110,
          sorter: (a, b) => a.option_1_count - b.option_1_count
        },
        {
          title: '選項2答數',
          dataIndex: 'option_2_count',
          key: 'option_2',
          width: 110,
          sorter: (a, b) => a.option_2_count - b.option_2_count
        },
        {
          title: '選項3答數',
          dataIndex: 'option_3_count',
          key: 'option_3',
          width: 110,
          sorter: (a, b) => a.option_3_count - b.option_3_count
        }
      ],

      // 🆕 表格欄位定義 - 圖片廣告 (移除圖片欄位，改在表格上方統一展示)
      imageTableColumns: [
        {
          title: '店家名稱',
          dataIndex: 'store_name',
          key: 'store_name',
          width: 180,
          scopedSlots: { customRender: 'storeName' }
        },
        {
          title: '日期',
          dataIndex: 'date',
          key: 'date',
          width: 120,
          scopedSlots: { customRender: 'date' },
          sorter: (a, b) => (a.date || '').localeCompare(b.date || '')
        },
        {
          title: '圖片檔名',
          dataIndex: 'image_filename',
          key: 'image_filename',
          width: 200,
          ellipsis: true
        },
        {
          title: '曝光次數',
          dataIndex: 'appear_count',
          key: 'appear',
          width: 100,
          sorter: (a, b) => a.appear_count - b.appear_count
        },
        {
          title: '點擊離開',
          dataIndex: 'exit_count',
          key: 'exit',
          width: 100,
          sorter: (a, b) => a.exit_count - b.exit_count
        }
      ],

      // 🆕 日期彙總表格欄位
      dateSummaryColumns: [
        {
          title: '日期',
          dataIndex: 'date',
          key: 'date',
          sorter: (a, b) => a.date.localeCompare(b.date)
        },
        { title: '出現次數', dataIndex: 'totalAppear', key: 'appear' },
        { title: '作答次數', dataIndex: 'totalAnswer', key: 'answer' },
        { title: '未作答', dataIndex: 'totalNoAnswer', key: 'no_answer' },
        { title: '答對', dataIndex: 'totalCorrect', key: 'correct' },
        { title: '答錯', dataIndex: 'totalWrong', key: 'wrong' },
        { title: '設備數', dataIndex: 'deviceCount', key: 'devices' }
      ]
    }
  },

  computed: {
    // 是否可以搜尋 (群組、檔期、版位、店家都需要選擇)
    canSearch () {
      return (
        this.searchForm.group_id &&
        this.searchForm.campaign_id &&
        this.searchForm.slot_id &&
        this.selectedDeviceMacs.length > 0
      )
    },

    // 是否有查詢結果
    hasResults () {
      return this.reportData.length > 0 || this.offlineDevices.length > 0
    },

    // 查詢進度百分比
    queryProgress () {
      if (this.queryStatus.totalDevices === 0) return 0
      return Math.round(
        (this.queryStatus.respondedDevices / this.queryStatus.totalDevices) *
          100
      )
    },

    // 🆕 是否有問答數據
    hasQuizData () {
      return this.reportData.some(
        (r) => r.ad_type === 'quiz' && r.questions && r.questions.length > 0
      )
    },

    // 🆕 設備回應率
    responseRate () {
      const total = this.queryStatus.totalDevices || (this.reportData.length + this.offlineDevices.length)
      if (total === 0) return '0'
      const responded = Math.max(this.queryStatus.respondedDevices || 0, this.reportData.length)
      return ((responded / total) * 100).toFixed(1)
    },

    respondedDeviceCount () {
      return Math.max(this.queryStatus.respondedDevices || 0, this.reportData.length)
    },

    unresponsiveDeviceCount () {
      if (!this.queryStatus.totalDevices) {
        return this.offlineDevices.length
      }

      if (this.queryStatus.status === 'completed' || this.queryStatus.status === 'partial' || this.queryStatus.status === 'timeout') {
        return this.offlineDevices.length
      }

      return Math.max(this.queryStatus.totalDevices - this.respondedDeviceCount, 0)
    },

    // 🆕 數據彙總統計
    summaryTotals () {
      let totalAppear = 0
      let totalAnswer = 0
      let totalCorrect = 0
      let totalWrong = 0
      let totalNoAnswer = 0

      this.reportData.forEach((result) => {
        if (result.ad_type === 'quiz' && result.questions) {
          result.questions.forEach((q) => {
            totalAppear += q.question_appear_count || 0
            totalAnswer += q.question_answered_count || 0
            totalCorrect += q.correct_count || 0
            totalWrong += q.wrong_count || 0
            totalNoAnswer +=
              (q.question_appear_count || 0) - (q.question_answered_count || 0)
          })
        } else if (result.images) {
          result.images.forEach((img) => {
            totalAppear += img.appear_count || 0
            totalAnswer += img.exit_count || 0 // 點擊離開視為互動
          })
        }
      })

      return {
        totalAppear,
        totalAnswer,
        totalCorrect,
        totalWrong,
        totalNoAnswer
      }
    },

    // 🆕 按日期彙總數據 (用於彙總視圖)
    dateSummaryData () {
      const dateMap = {}

      this.reportData.forEach((result) => {
        if (result.ad_type === 'quiz' && result.questions) {
          result.questions.forEach((q) => {
            const date = q.date || '未知日期'
            if (!dateMap[date]) {
              dateMap[date] = {
                date,
                totalAppear: 0,
                totalAnswer: 0,
                totalCorrect: 0,
                totalWrong: 0,
                totalNoAnswer: 0,
                deviceCount: 0,
                devices: new Set()
              }
            }
            dateMap[date].totalAppear += q.question_appear_count || 0
            dateMap[date].totalAnswer += q.question_answered_count || 0
            dateMap[date].totalCorrect += q.correct_count || 0
            dateMap[date].totalWrong += q.wrong_count || 0
            dateMap[date].totalNoAnswer +=
              (q.question_appear_count || 0) - (q.question_answered_count || 0)
            if (!dateMap[date].devices.has(result.device_mac)) {
              dateMap[date].devices.add(result.device_mac)
              dateMap[date].deviceCount++
            }
          })
        }
      })

      return Object.values(dateMap).sort((a, b) =>
        b.date.localeCompare(a.date)
      )
    },

    // 🆕 展平的問答數據 (用於表格視圖)
    flattenedQuizData () {
      const data = []
      this.reportData.forEach((result) => {
        if (result.ad_type === 'quiz' && result.questions) {
          result.questions.forEach((q, idx) => {
            data.push({
              uniqueKey: `${result.device_mac}-${q.date || idx}-${idx}`,
              device_mac: result.device_mac,
              store_name: result.store_name,
              date: q.date,
              question_title: q.question_title,
              question_appear_count: q.question_appear_count || 0,
              question_answered_count: q.question_answered_count || 0,
              correct_count: q.correct_count || 0,
              wrong_count: q.wrong_count || 0,
              option_1_count: q.option_1_count || 0,
              option_2_count: q.option_2_count || 0,
              option_3_count: q.option_3_count || 0,
              no_answer_count:
                (q.question_appear_count || 0) -
                (q.question_answered_count || 0),
              response_rate:
                (q.question_appear_count || 0) > 0
                  ? `${(
                      ((q.question_answered_count || 0) /
                        (q.question_appear_count || 0)) *
                      100
                    ).toFixed(1)}%`
                  : '0%',
              accuracy_rate:
                (q.question_answered_count || 0) > 0
                  ? `${(
                      ((q.correct_count || 0) /
                        (q.question_answered_count || 0)) *
                      100
                    ).toFixed(1)}%`
                  : '0%'
            })
          })
        }
      })
      return data
    },

    // 🆕 展平的圖片數據 (用於表格視圖)
    flattenedImageData () {
      const data = []
      this.reportData.forEach((result) => {
        if (result.images && result.images.length > 0) {
          result.images.forEach((img, idx) => {
            data.push({
              uniqueKey: `${result.device_mac}-${img.image_filename}-${idx}`,
              device_mac: result.device_mac,
              store_name: result.store_name,
              date: img.date,
              image_filename: img.image_filename || img.exit_image,
              appear_count: img.appear_count || 0,
              exit_count: img.exit_count || 0
            })
          })
        }
      })
      return data
    }
  },

  created () {
    // 初始化日期範圍
    this.onDateChange([moment().subtract(30, 'days'), moment()])
    // 載入群組資料
    this.fetchGroupOptions()
  },

  beforeDestroy () {
    // 清理 SSE 連線
    this.closeEventSource()
  },

  methods: {
    // ===== 資料載入 =====

    // 獲取群組選項
    async fetchGroupOptions () {
      this.loadingGroups = true
      try {
        const response = await this.axios.get(
          `${this.baseURL}${this.basePort}/ads/groups`
        )
        if (response.data.message === 'success') {
          this.groupOptions = response.data.data || []
        }
      } catch (error) {
        console.error('獲取群組失敗:', error)
        this.$message.error('獲取群組資料失敗')
      } finally {
        this.loadingGroups = false
      }
    },

    // 獲取檔期選項
    async fetchCampaignOptions (groupId) {
      this.loadingCampaigns = true
      try {
        const response = await this.axios.get(
          `${this.baseURL}${this.basePort}/adCampaigns/list`,
          {
            params: { group_id: groupId }
          }
        )
        if (response.data.message === 'success') {
          this.campaignOptions = response.data.data || []
        }
      } catch (error) {
        console.error('獲取檔期失敗:', error)
        this.$message.error('獲取檔期資料失敗')
      } finally {
        this.loadingCampaigns = false
      }
    },

    // 獲取店家選項
    fetchStoreOptions (groupId) {
      const group = this.groupOptions.find((g) => g.id === groupId)
      if (group && group.stores_data) {
        this.storeOptions = group.stores_data.map((store) => ({
          device_mac: store.device_mac,
          store_name: store.device_name,
          city: store.city,
          status: store.status || 'active'
        }))
        // 預設全選
        this.selectedDeviceMacs = this.storeOptions.map((s) => s.device_mac)
      } else {
        this.storeOptions = []
        this.selectedDeviceMacs = []
      }
    },

    // 🆕 載入檔期圖片
    async loadCampaignImages (campaignId) {
      if (!campaignId) {
        this.campaignImages = []
        return
      }

      this.loadingCampaignImages = true
      try {
        const response = await this.axios.get(
          `${this.baseURL}${this.basePort}/adCampaigns/${campaignId}/images`
        )
        if (response.data.message === 'success') {
          this.campaignImages = response.data.data.images || []
        } else {
          this.campaignImages = []
        }
      } catch (error) {
        console.error('載入檔期圖片失敗:', error)
        this.campaignImages = []
      } finally {
        this.loadingCampaignImages = false
      }
    },

    // 🆕 取得檔期圖片 URL
    getCampaignImageUrl (filename) {
      if (!this.selectedCampaign || !filename) return ''
      return `${this.baseURL}${this.basePort}/adCampaigns/${this.selectedCampaign.campaign_id}/images/${filename}`
    },

    // ===== 事件處理 =====

    // 群組變更
    handleGroupChange (groupId) {
      // 清空相關選項
      this.searchForm.campaign_id = undefined
      this.campaignOptions = []
      this.selectedCampaign = null
      this.reportData = []
      this.offlineDevices = []
      this.campaignImages = [] // 🆕 清空檔期圖片

      if (groupId) {
        this.fetchCampaignOptions(groupId)
        this.fetchStoreOptions(groupId)
      } else {
        this.storeOptions = []
        this.selectedDeviceMacs = []
      }
    },

    // 檔期變更
    handleCampaignChange (campaignId) {
      // 🆕 檔期變更時，清空現有查詢結果，避免誤導
      this.reportData = []
      this.offlineDevices = []
      this.summaryStats = null

      this.selectedCampaign = this.campaignOptions.find(
        (c) => c.campaign_id === campaignId
      )

      // 如果選擇了檔期，自動設定日期範圍與版位
      if (this.selectedCampaign) {
        const start = moment(this.selectedCampaign.start_datetime)
        const end = moment(this.selectedCampaign.end_datetime)
        this.dateRange = [start, end]
        this.onDateChange([start, end])

        // 自動帶入檔期的版位設定
        if (this.selectedCampaign.slot_id) {
          this.searchForm.slot_id = this.selectedCampaign.slot_id
        }

        // 🆕 載入該檔期的圖片
        this.loadCampaignImages(campaignId)
      } else {
        // 清除檔期時，重置版位選擇與圖片
        this.searchForm.slot_id = undefined
        this.campaignImages = []
      }
    },

    // 日期變更
    onDateChange (dates) {
      if (dates && dates.length === 2) {
        this.searchForm.date_from = dates[0].format('YYYY-MM-DD')
        this.searchForm.date_to = dates[1].format('YYYY-MM-DD')
      } else {
        this.searchForm.date_from = undefined
        this.searchForm.date_to = undefined
      }
    },

    // 全選店家
    selectAllStores () {
      this.selectedDeviceMacs = this.storeOptions.map((s) => s.device_mac)
    },

    // 清除所有店家選擇
    clearAllStores () {
      this.selectedDeviceMacs = []
    },

    // 搜尋 - 發起查詢
    async handleSearch () {
      if (!this.canSearch) {
        this.$message.warning('請完整填寫所有查詢條件')
        return
      }

      this.loading = true
      this.reportData = []
      this.offlineDevices = []

      // 重置狀態
      this.queryStatus = {
        isQuerying: false,
        requestId: null,
        totalDevices: 0,
        respondedDevices: 0,
        timeoutSeconds: 35,
        status: null
      }

      try {
        // 準備請求參數
        const payload = {
          group_id: this.searchForm.group_id,
          campaign_id: this.searchForm.campaign_id || null,
          slot_id: this.searchForm.slot_id,
          device_macs: this.selectedDeviceMacs,
          date_from: this.searchForm.date_from,
          date_to: this.searchForm.date_to
        }

        console.log('📡 發起查詢請求 (POST /analytics/query):', payload)

        const response = await this.axios.post(
          `${this.baseURL}${this.basePort}/analytics/query`,
          payload
        )

        if (response.data.message === 'success') {
          const data = response.data.data
          console.log('✅ 查詢指令發送成功:', data)

          // 1. 更新查詢狀態
          this.queryStatus = {
            isQuerying: true, // 開啟進度條顯示
            requestId: data.requestId,
            totalDevices: data.expectedDevices,
            respondedDevices: 0,
            timeoutSeconds: data.timeoutSeconds || 35,
            status: 'pending'
          }

          this.$message.info(data.message || '查詢已發起，正在等待設備回應...')

          // 2. 連接 SSE (使用 API 回傳的 endpoint)
          if (data.sseEndpoint) {
            this.connectSSE(data.sseEndpoint)
          }
        } else {
          throw new Error(response.data.errorMsg || '發起查詢失敗')
        }
      } catch (error) {
        console.error('查詢失敗:', error)
        this.$message.error(
          error.response?.data?.errorMsg || error.message || '查詢失敗'
        )
        // 發生錯誤重置狀態
        this.queryStatus.isQuerying = false
        this.loading = false
      }
    },

    // 處理 Summary API 回傳結果
    handleSummaryResult (data) {
      console.log('📊 收到 Summary 資料:', data)

      // 判斷廣告類型
      let adType = 'image'
      // 若是 Slot 1 且有 quiz 資料，優先歸類為 quiz
      if (this.searchForm.slot_id === 1 && data.quiz && data.quiz.length > 0) {
        adType = 'quiz'
      }

      // 建立總覽資料項目
      const summaryItem = {
        device_mac: 'summary', // 虛擬 MAC
        store_name: '活動數據總覽 (全設備)',
        status: 'success',
        ad_type: adType,
        // 對應前端顯示需要的欄位結構
        images: data.carousel
          ? data.carousel.map((item) => ({
            ...item,
            exit_count: item.click_count, // click_count 對應 exit_count
            exit_image: item.image_filename // image_filename 對應 exit_image
          }))
          : [],
        // 問答資料 (映射欄位)
        questions: (data.quiz || []).map((quiz) => ({
          ...quiz,
          question_title: quiz.question_title || `活動 #${quiz.campaign_id}`,
          question_appear_count: quiz.appear_count || 0,
          question_answered_count: quiz.answer_count || 0,
          correct_count: quiz.correct_count || 0,
          wrong_count: quiz.wrong_count || 0,
          date: quiz.date // 保留日期資訊
        }))
      }

      this.reportData = [summaryItem]

      this.queryStatus.status = 'completed'
      this.$message.success('查詢完成')
    },

    // 建立 SSE 連線
    connectSSE (endpoint) {
      this.closeEventSource()

      const sseUrl = `${this.baseURL}${this.basePort}${endpoint}`
      console.log('🔗 建立 SSE 連線:', sseUrl)

      // ✅ 使用 Polyfill，加上 withCredentials
      this.eventSource = new EventSourcePolyfill(sseUrl, {
        withCredentials: true,
        heartbeatTimeout: 180000
      })

      // ✅ 用 onmessage 統一處理所有訊息
      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          console.log('📨 收到訊息:', data)

          switch (data.type) {
            case 'connected':
              console.log('✅ SSE 連線成功')
              break
            case 'device_data':
              // 🆕 即時處理單一設備回應，立即顯示
              this.handleDeviceData(data)
              break
            case 'progress':
              this.queryStatus.respondedDevices = data.received || 0
              break
            case 'result':
              // 最終結果：處理離線設備和結束狀態
              this.handleQueryResult(data)
              break
          }
        } catch (e) {
          console.error('解析資料失敗:', e)
        }
      }

      this.eventSource.onerror = (error) => {
        console.error('❌ SSE 連線錯誤:', error)
        this.closeEventSource()
        this.queryStatus.isQuerying = false
        this.loading = false

        if (!this.hasResults) {
          this.$message.error('連線中斷，請重新查詢')
        }
      }
    },

    // 🆕 處理單一設備即時回應
    handleDeviceData (data) {
      console.log('📱 收到設備資料:', data.deviceMac)

      const deviceData = data.data || {}

      // 判斷廣告類型
      const hasQuiz = deviceData.quiz && deviceData.quiz.length > 0
      const adType =
        this.searchForm.slot_id === 1 && hasQuiz ? 'quiz' : 'image'

      // 轉換資料結構
      const deviceResult = {
        device_mac: data.deviceMac,
        store_name: deviceData.store_name || data.deviceMac,
        status: 'success',
        ad_type: adType,
        receivedAt: data.receivedAt,

        // 圖片輪播資料
        images: (deviceData.carousel || []).map((img) => ({
          image_filename: img.image_filename,
          exit_image: img.image_filename,
          appear_count: img.appear_count || 0,
          exit_count: img.click_count || 0,
          date: img.date,
          image_index: img.image_index
        })),

        // 問答資料
        questions: (deviceData.quiz || []).map((quiz) => ({
          ...quiz,
          question_title: quiz.question_title || `活動 #${quiz.campaign_id}`,
          question_appear_count: quiz.appear_count || 0,
          question_answered_count: quiz.answer_count || 0,
          correct_count: quiz.correct_count || 0,
          wrong_count: quiz.wrong_count || 0,
          date: quiz.date
        }))
      }

      // 🔥 立即追加到 reportData，觸發畫面更新
      this.reportData.push(deviceResult)
    },

    // 處理最終查詢結果（離線設備、結束狀態）
    handleQueryResult (data) {
      this.closeEventSource()

      this.queryStatus.status = data.status // 'completed', 'partial', 'timeout'
      this.queryStatus.isQuerying = false
      this.loading = false

      // ⚠️ 檢查是否有「提早交卷」而被 SSE 漏接的機台，把他們補回 reportData
      if (data.results && data.results.length > 0) {
        const existingMacs = this.reportData.map((r) => r.device_mac)
        data.results.forEach((deviceData) => {
          if (!existingMacs.includes(deviceData.deviceMac)) {
            console.log('🔄 補回漏接的早鳥設備資料:', deviceData.deviceMac)
            this.handleDeviceData(deviceData)
          }
        })
      }

      // 只處理離線設備和統計資訊
      this.offlineDevices = data.offlineDevices || []
      this.summaryStats = data.summary || null

      // 顯示結果訊息
      const respondedCount =
        data.summary?.respondedDevices || this.reportData.length
      const offlineCount =
        data.summary?.offlineCount || this.offlineDevices.length

      if (data.status === 'completed') {
        this.$message.success(`查詢完成！共 ${respondedCount} 台設備回應`)
      } else if (data.status === 'partial' || data.status === 'timeout') {
        this.$message.warning(
          `部分設備未回應，共 ${respondedCount} 台回應，${offlineCount} 台離線`
        )
      }
    },

    // 關閉 SSE 連線
    closeEventSource () {
      if (this.eventSource) {
        this.eventSource.close()
        this.eventSource = null
      }
    },

    // 重置搜尋
    resetSearch () {
      this.closeEventSource()

      this.searchForm = {
        group_id: undefined,
        campaign_id: undefined,
        slot_id: undefined,
        date_from: undefined,
        date_to: undefined
      }
      this.dateRange = [moment().subtract(30, 'days'), moment()]
      this.onDateChange(this.dateRange)

      this.selectedDeviceMacs = []
      this.campaignOptions = []
      this.storeOptions = []
      this.selectedCampaign = null
      this.reportData = []
      this.offlineDevices = []
      this.summaryStats = null

      this.queryStatus = {
        isQuerying: false,
        requestId: null,
        totalDevices: 0,
        respondedDevices: 0,
        timeoutSeconds: 20,
        status: null
      }
    },

    // 🆕 處理匯出選單點擊
    handleExportMenuClick ({ key }) {
      this.exportData(key)
    },

    // CSV 匯出 - 更新為支援多種匯出類型
    async exportData (exportType = 'detailed') {
      if (!this.hasResults) {
        this.$message.warning('沒有可匯出的資料')
        return
      }

      this.exporting = true
      try {
        let exportData, fileName

        switch (exportType) {
          case 'summary':
            exportData = this.prepareSummaryData()
            fileName = this.getExportFileName('summary')
            break
          case 'complete':
          default:
            exportData = this.prepareCompleteData()
            fileName = this.getExportFileName('complete')
        }

        // 建立 CSV 內容
        const csvContent = this.generateCSV(exportData)

        // 下載檔案
        const blob = new Blob(['\uFEFF' + csvContent], {
          type: 'text/csv;charset=utf-8;'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${fileName}_${this.getSlotName(
          this.searchForm.slot_id
        )}_${moment().format('YYYYMMDD_HHmmss')}.csv`
        link.click()
        window.URL.revokeObjectURL(url)

        this.$message.success('報表匯出成功')
      } catch (error) {
        console.error('匯出失敗:', error)
        this.$message.error('匯出報表失敗')
      } finally {
        this.exporting = false
      }
    },

    // 相容性方法 (向後相容)
    async exportCSV () {
      await this.exportData('complete')
    },

    // 🆕 準備彙總統計資料
    prepareSummaryData () {
      const summaryRows = [
        {
          項目: '=== 查詢資訊 ===',
          內容: '',
          數值: '',
          備註: ''
        },
        {
          項目: '查詢時間',
          內容: moment().format('YYYY-MM-DD HH:mm:ss'),
          數值: '',
          備註: ''
        },
        {
          項目: '查詢期間',
          內容: `${this.searchForm.date_from} 至 ${this.searchForm.date_to}`,
          數值: '',
          備註: ''
        },
        {
          項目: '廣告版位',
          內容: this.getSlotName(this.searchForm.slot_id),
          數值: '',
          備註: ''
        },
        {
          項目: '=== 設備統計 ===',
          內容: '',
          數值: '',
          備註: ''
        },
        {
          項目: '已回應設備',
          內容: `${this.reportData.length} 台`,
          數值: this.reportData.length,
          備註: ''
        },
        {
          項目: '離線設備',
          內容: `${this.offlineDevices.length} 台`,
          數值: this.offlineDevices.length,
          備註: ''
        },
        {
          項目: '回應率',
          內容: `${this.responseRate}%`,
          數值: this.responseRate,
          備註: ''
        },
        {
          項目: '=== 數據統計 ===',
          內容: '',
          數值: '',
          備註: ''
        },
        {
          項目: '總曝光次數',
          內容: this.formatNumber(this.summaryTotals.totalAppear),
          數值: this.summaryTotals.totalAppear,
          備註: '所有設備所有內容的曝光總和'
        },
        {
          項目: '總互動次數',
          內容: this.formatNumber(this.summaryTotals.totalAnswer),
          數值: this.summaryTotals.totalAnswer,
          備註: '作答次數或點擊離開次數的總和'
        },
        {
          項目: '總未作答次數',
          內容: this.formatNumber(this.summaryTotals.totalNoAnswer),
          數值: this.summaryTotals.totalNoAnswer,
          備註: '曝光但未作答的次數總和'
        }
      ]

      // 如果是問答，加入問答特有統計
      if (this.searchForm.slot_id === 1 && this.hasQuizData) {
        summaryRows.push(
          {
            項目: '總答對次數',
            內容: this.formatNumber(this.summaryTotals.totalCorrect),
            數值: this.summaryTotals.totalCorrect,
            備註: '所有設備答對次數總和'
          },
          {
            項目: '總答錯次數',
            內容: this.formatNumber(this.summaryTotals.totalWrong),
            數值: this.summaryTotals.totalWrong,
            備註: '所有設備答錯次數總和'
          },
          {
            項目: '整體正確率',
            內容:
              this.summaryTotals.totalAnswer > 0
                ? `${(
                    (this.summaryTotals.totalCorrect /
                      this.summaryTotals.totalAnswer) *
                    100
                  ).toFixed(1)}%`
                : '0%',
            數值:
              this.summaryTotals.totalAnswer > 0
                ? (
                  (this.summaryTotals.totalCorrect /
                      this.summaryTotals.totalAnswer) *
                    100
                ).toFixed(1)
                : 0,
            備註: '所有答題中答對的比例'
          }
        )
      }

      return summaryRows
    },

    // 🆕 準備詳細明細資料 (原 prepareExportData 的改進版)
    prepareDetailedData () {
      const rows = []

      this.reportData.forEach((result) => {
        if (this.searchForm.slot_id === 1) {
          // 版位1: 圖片或問答
          if (result.ad_type === 'image' && result.images) {
            result.images.forEach((img) => {
              rows.push({
                店家名稱: result.store_name,
                設備MAC: result.device_mac,
                回應時間: result.receivedAt
                  ? moment(result.receivedAt).format('YYYY-MM-DD HH:mm:ss')
                  : '',
                日期: img.date || '未知',
                廣告類型: '圖片輪播',
                內容: img.exit_image || img.image_filename,
                曝光次數: img.appear_count || 0,
                點擊離開: img.exit_count || 0,
                點擊率:
                  img.appear_count > 0
                    ? `${((img.exit_count / img.appear_count) * 100).toFixed(
                        2
                      )}%`
                    : '0%',
                圖片索引: img.image_index || 0
              })
            })
          } else if (result.ad_type === 'quiz' && result.questions) {
            result.questions.forEach((q) => {
              rows.push({
                店家名稱: result.store_name,
                設備MAC: result.device_mac,
                回應時間: result.receivedAt
                  ? moment(result.receivedAt).format('YYYY-MM-DD HH:mm:ss')
                  : '',
                日期: q.date || '未知',
                廣告類型: '問答',
                內容: q.question_title,
                出現次數: q.question_appear_count || 0,
                作答次數: q.question_answered_count || 0,
                未作答次數:
                  (q.question_appear_count || 0) -
                  (q.question_answered_count || 0),
                答對次數: q.correct_count || 0,
                答錯次數: q.wrong_count || 0,
                選項1作答數: q.option_1_count || 0,
                選項2作答數: q.option_2_count || 0,
                選項3作答數: q.option_3_count || 0,
                作答率:
                  q.question_appear_count > 0
                    ? `${(
                        (q.question_answered_count / q.question_appear_count) *
                        100
                      ).toFixed(1)}%`
                    : '0%',
                正確率:
                  q.question_answered_count > 0
                    ? `${(
                        (q.correct_count / q.question_answered_count) *
                        100
                      ).toFixed(1)}%`
                    : '0%'
              })
            })
          }
        } else {
          // 版位2/3: 輪播圖
          if (result.images) {
            result.images.forEach((img) => {
              rows.push({
                店家名稱: result.store_name,
                設備MAC: result.device_mac,
                回應時間: result.receivedAt
                  ? moment(result.receivedAt).format('YYYY-MM-DD HH:mm:ss')
                  : '',
                日期: img.date || '未知',
                廣告類型: '圖片輪播',
                內容: img.image_filename,
                曝光次數: img.appear_count || 0,
                圖片索引: img.image_index || 0
              })
            })
          }
        }
      })

      return rows
    },

    // 🆕 準備完整報表資料
    prepareCompleteData () {
      // 直接回傳詳細明細資料，保留所有原始欄位（包含選項作答數等）
      return this.prepareDetailedData()
    },

    // 產生 CSV - 改進版
    generateCSV (data) {
      if (!data || data.length === 0) {
        console.warn('沒有資料可匯出')
        return '查詢結果,無資料\n無資料,請重新查詢'
      }

      try {
        const headers = Object.keys(data[0])

        // 處理標題行
        const headerRow = headers.map((h) => `"${h}"`).join(',')

        // 處理資料行
        const dataRows = data.map((row) => {
          return headers
            .map((header) => {
              let value = row[header]
              // 處理 null/undefined
              if (value === null || value === undefined) {
                value = ''
              }
              // 處理包含逗號或引號的內容
              value = String(value).replace(/"/g, '""')
              return `"${value}"`
            })
            .join(',')
        })

        return [headerRow, ...dataRows].join('\n')
      } catch (error) {
        console.error('CSV 生成失敗:', error)
        return '錯誤,CSV生成失敗\n請聯繫技術支援'
      }
    },

    // 🆕 取得檔案名稱 (根據匯出類型)
    getExportFileName (type) {
      const timestamp = moment().format('YYYYMMDD_HHmmss')
      const slotName = this.getSlotName(this.searchForm.slot_id)

      const typeNames = {
        summary: '彙總統計',
        complete: '完整報表'
      }

      return `廣告追蹤報表_${typeNames[type]}_${slotName}_${timestamp}.csv`
    },

    // ===== 工具函數 =====

    // 根據 MAC 取得店家名稱
    getStoreNameByMac (mac) {
      const store = this.storeOptions.find((s) => s.device_mac === mac)
      return store?.store_name || mac
    },

    // 數字格式化
    formatNumber (number) {
      return (number || 0).toLocaleString()
    },

    // 日期範圍格式化
    formatDateRange (start, end) {
      if (!start || !end) return ''
      return `${moment(start).format('YYYY/MM/DD')} - ${moment(end).format(
        'YYYY/MM/DD'
      )}`
    },

    // 檔期狀態顏色
    getCampaignStatusColor (campaign) {
      const status = this.getCampaignStatus(campaign)
      return status.color
    },

    // 檔期狀態文字
    getCampaignStatusText (campaign) {
      const status = this.getCampaignStatus(campaign)
      return status.text
    },

    // 檔期狀態判斷
    getCampaignStatus (campaign) {
      const now = moment()
      const start = moment(campaign.start_datetime)
      const end = moment(campaign.end_datetime)

      if (now.isBefore(start)) {
        return { color: 'blue', text: '未開始' }
      } else if (now.isAfter(end)) {
        return { color: 'default', text: '已結束' }
      } else {
        return { color: 'green', text: '進行中' }
      }
    },

    // 查詢狀態顏色
    getQueryStatusColor () {
      const progress = this.queryProgress
      if (progress >= 100) return 'green'
      if (progress >= 50) return 'blue'
      return 'orange'
    },

    // 獲取版位名稱
    getSlotName (slotId) {
      const slotNames = {
        1: '版位1 (主廣告區)',
        2: '版位2 (次廣告區)',
        3: '版位3 (輔助廣告區)'
      }
      return slotNames[slotId] || `版位${slotId}`
    },

    // 獲取圖片 URL
    getImageUrl (filename) {
      if (!filename) return ''
      // 如果已經是完整 URL，直接返回
      if (filename.startsWith('http')) return filename
      return `${this.baseURL}${this.basePort}/uploads/ads/${filename}`
    },

    // 圖片預覽
    showImagePreview (imageUrl) {
      this.previewImage = imageUrl
      this.previewVisible = true
    },

    // 圖片載入錯誤處理
    handleImageError (event) {
      event.target.src =
        'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNMzIgMjhDMjguNjg2MyAyOCAyNiAzMC42ODYzIDI2IDM0QzI2IDM3LjMxMzcgMjguNjg2MyA0MCAzMiA0MEMzNS4zMTM3IDQwIDM4IDM3LjMxMzcgMzggMzRDMzggMzAuNjg2MyAzNS4zMTM3IDI4IDMyIDI4WiIgZmlsbD0iI0JGQkZCRiIvPjwvc3ZnPg=='
    }
  }
}
</script>

<style scoped>
/* 步驟圓圈樣式 */
.rounded-full {
  transition: all 0.3s ease;
}

/* 卡片 hover 效果 */
.bg-white {
  transition: all 0.3s ease;
}

.bg-white:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Checkbox 群組樣式 */
:deep(.ant-checkbox-group) {
  width: 100%;
}

/* 店家列表滾動條美化 */
.max-h-64::-webkit-scrollbar {
  width: 6px;
}

.max-h-64::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.max-h-64::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.max-h-64::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* 進度條動畫 */
:deep(.ant-progress-bg) {
  transition: all 0.5s ease;
}

/* 結果卡片動畫 */
.space-y-6 > div {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 禁用狀態樣式 */
.opacity-50 {
  pointer-events: none;
}

/* 響應式調整 */
@media (max-width: 768px) {
  .grid-cols-1.md\\:grid-cols-2.lg\\:grid-cols-3 {
    grid-template-columns: 1fr;
  }
}
</style>
